/*
 * Copyright 2026 Sony Corporation
 */

import { Alert, Box, Button, CircularProgress, Snackbar } from '@mui/material';
import type { Device, DeviceStateResponse } from '@ips-device-verse/twin-service/schema';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { WorldHeader } from '../components/WorldHeader';
import { ActivityBar, type ActivityView } from '../components/panels/ActivityBar';
import { ACTIVITY_ITEMS } from '../components/panels/activity-items';
import { AddDeviceDialog } from '../components/panels/AddDeviceDialog';
import { BomDetail } from '../components/panels/BomDetail';
import { DeployPanel, type BomSection } from '../components/panels/DeployPanel';
import { DeviceInspector } from '../components/panels/DeviceInspector';
import { DeviceListPanel } from '../components/panels/DeviceListPanel';
import { ChatPanel } from '../components/chat/ChatPanel';
import { CollapsedRail } from '../components/panels/CollapsedRail';
import { GatewayDetail } from '../components/panels/GatewayDetail';
import { GatewaySetupDialog } from '../components/panels/GatewaySetupDialog';
import { BabylonCanvas } from '../components/scene/BabylonCanvas';
import { CoveragePanel } from '../components/scene/CoveragePanel';
import { ViewAngleButtons } from '../components/scene/ViewAngleButtons';
import { CAMERA_STUB_PROMPTS } from '../services/device-agent-stub';
import { applyCameraOps, plannedTransform, proposedOptics, type SceneBounds } from '../device-ops/apply';
import { digestCoverage, digestDevices } from '../device-ops/digest';
import { useDeviceAgent } from '../hooks/use-device-agent';
import { touchesTwin, type DeviceOp } from '../device-ops/schema';
import { useAuth } from '../contexts/auth-context';
import { DEFAULT_COVERAGE_OPTIONS, coverageCamera } from '../coverage/coverage';
import { REFERENCE_HEIGHT_M, type SubjectPlacement } from '../coverage/subject';
import { useCameraOptics } from '../hooks/use-camera-optics';
import { useCameraPreview } from '../hooks/use-camera-preview';
import { useCameraRigs } from '../hooks/use-camera-rigs';
import { useCaptureResponder } from '../hooks/use-capture-responder';
import { useCoverage, type CoverageSettings } from '../hooks/use-coverage';
import { useCreativeLook } from '../hooks/use-creative-look';
import { useRealtime } from '../hooks/use-realtime';
import { useScene } from '../hooks/use-scene';
import { useGateways, useWorld } from '../hooks/use-world';
import type { SceneEditor } from '../scene/scene-editor';

export const WorldEditor = () => {
  const { world_id: worldId = '' } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const { data, error, loading, reload } = useWorld(worldId);
  const gateways = useGateways(worldId);

  // Selection lives in the URL so a chosen device is shareable and survives
  // browser back/forward.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('device') ?? undefined;

  const scene = useScene({ worldId, sceneId: data?.world.scene_id });
  const realtime = useRealtime({ worldId, onSceneChanged: scene.applyRemote });

  // Rows newer than the list: a write's own answer, or another tab's pushed
  // over the socket. `updated_at` decides, so a late frame cannot roll one back.
  const [rows, setRows] = useState<Record<string, Device>>({});
  const noteDevice = useCallback((device: Device) => {
    setRows((previous) => {
      const known = previous[device.device_id];
      return known && known.updated_at >= device.updated_at ? previous : { ...previous, [device.device_id]: device };
    });
  }, []);
  useEffect(
    () =>
      realtime.subscribe((event) => {
        if (event.type === 'device_changed' && event.world_id === worldId) {
          noteDevice(event.device);
        }
      }),
    [realtime, worldId, noteDevice]
  );
  const devices = useMemo(
    () =>
      (data?.devices ?? []).map((device) => {
        const row = rows[device.device_id];
        return row && row.updated_at > device.updated_at ? row : device;
      }),
    [data?.devices, rows]
  );

  const optics = useCameraOptics({ worldId, devices, onSaved: noteDevice });
  // Off by default: it is the most expensive pass in the preview.
  const [depthOfFieldShown, setDepthOfFieldShown] = useState(false);

  // Devices the assistant has proposed and nobody has confirmed. They are drawn
  // and nothing else: no row, no twin, no shadow until Apply.
  const [proposed, setProposed] = useState<readonly DeviceOp[]>([]);
  const editorRef = useRef<SceneEditor>(null);

  /**
   * The scene's own extent, which every distance the assistant proposes derives
   * from (devplan §4.7). Held as a value rather than read from the editor when
   * needed, because what it places has to be drawn during a render.
   */
  const [bounds, setBounds] = useState<SceneBounds>({ center: [0, 0, 0], radius: 1 });

  const readBounds = useCallback(() => {
    const active = editorRef.current?.activeBounds();
    if (active) {
      setBounds({ center: [active.center.x, active.center.y, active.center.z], radius: active.radius });
    }
  }, []);

  // The scene arrives asynchronously, so its extent is only known once the
  // switch settles.
  useEffect(() => {
    if (scene.state.status === 'idle') {
      readBounds();
    }
  }, [scene.state, readBounds]);

  const previewRigs = useMemo(
    () =>
      proposed.flatMap((op, index) =>
        op.op === 'add_device'
          ? [
              {
                deviceId: `proposed:${index}`,
                transform: plannedTransform(op, bounds),
                pending: true,
                optics: proposedOptics(op),
              },
            ]
          : []
      ),
    [proposed, bounds]
  );

  const rigs = useCameraRigs({
    worldId,
    devices,
    selectedId,
    live: realtime.states,
    preview: previewRigs,
    optics: optics.optics,
  });

  // Off until asked for: the subject is an object in the scene, and a person
  // nobody placed standing in the middle of the room would be a surprise.
  const [coverageOn, setCoverageOn] = useState(false);
  const [coverageSettings, setCoverageSettings] = useState<CoverageSettings>({
    ...DEFAULT_COVERAGE_OPTIONS,
    mode: 'detail',
  });
  const [subjectMoved, setSubjectMoved] = useState<SubjectPlacement>();
  // In the middle of the scene, on the floor, until someone moves it.
  const placement = useMemo<SubjectPlacement>(
    () =>
      subjectMoved ?? {
        position: [bounds.center[0], 0, bounds.center[2]],
        height: REFERENCE_HEIGHT_M,
        heading: 0,
      },
    [subjectMoved, bounds]
  );
  // Proposals count too, so a suggested rig can be judged before it is applied.
  const coverageCameras = coverageOn
    ? [
        ...devices.flatMap((device) => {
          const pose = rigs.poses[device.device_id];
          const lens = optics.optics[device.device_id];
          return pose && lens ? [coverageCamera(device.device_id, pose, lens)] : [];
        }),
        ...previewRigs.map((rig) => coverageCamera(rig.deviceId, rig.transform, rig.optics)),
      ]
    : [];
  const coverage = useCoverage({
    enabled: coverageOn,
    cameras: coverageCameras,
    settings: coverageSettings,
    placement,
  });

  const capture = useCaptureResponder({
    worldId,
    subscribe: realtime.subscribe,
    subjectOf: (deviceId) => {
      const pose = rigs.poses[deviceId];
      const lens = optics.optics[deviceId];
      return pose && lens ? { pose, optics: lens } : undefined;
    },
    ready: scene.state.activeId !== undefined && scene.state.status !== 'loading',
  });

  // Held here, not in the inspector: the preview renders the same values the
  // sliders show, and two copies of them would drift.
  const look = useCreativeLook({
    worldId,
    deviceId: selectedId,
    live: selectedId ? realtime.states[selectedId] : undefined,
  });
  const preview = useCameraPreview({
    deviceId: selectedId,
    look: look.values,
    optics: selectedId ? optics.optics[selectedId] : undefined,
    depthOfField: depthOfFieldShown,
  });

  const [addingCamera, setAddingCamera] = useState(false);
  const [downloadingBundle, setDownloadingBundle] = useState(false);
  // Opens on the devices, which is what the editor is for; the scene is chosen
  // once and then rarely touched.
  const [view, setView] = useState<ActivityView | undefined>('devices');
  const [bomSection, setBomSection] = useState<BomSection>();
  const [fetchedStates, setFetchedStates] = useState<Record<string, DeviceStateResponse>>({});
  const [refreshing, setRefreshing] = useState(false);
  // Which of the two views the right-hand panel is showing. Opening on the
  // device because a world with devices in it is usually opened to look at one.
  const [inspecting, setInspecting] = useState(true);
  // The rail still says which view is chosen while its list is out of the way.
  const [sideOpen, setSideOpen] = useState(true);
  const [sceneErrorSeen, setSceneErrorSeen] = useState<Error>();
  const sceneError = scene.state.error === sceneErrorSeen ? undefined : scene.state.error;
  const [openError, setOpenError] = useState<string>();

  /**
   * A world has one scene, so opening it and making the first one are the same
   * click: `scene_id` is only set once a scene has been published, and a draft
   * that was never saved is still the scene this world is building.
   */
  const openScene = useCallback(async () => {
    setOpenError(undefined);
    try {
      const [existing] = await api.listScenes(worldId);
      const scene = existing ?? (await api.createScene(worldId, { name: 'Scene' }));
      navigate(`/worlds/${encodeURIComponent(worldId)}/scenes/${encodeURIComponent(scene.scene_id)}/edit`);
    } catch (cause) {
      setOpenError(cause instanceof Error ? cause.message : 'The scene could not be opened.');
    }
  }, [api, navigate, worldId]);

  // A manual read and a pushed event are the same document from the same place,
  // so the newer version wins rather than the later arrival.
  const gatewayStates = useMemo(() => {
    const merged = { ...fetchedStates };
    for (const [deviceId, live] of Object.entries(realtime.states)) {
      const fetched = merged[deviceId];
      if (!fetched || (live.version ?? 0) >= (fetched.version ?? 0)) {
        merged[deviceId] = live;
      }
    }
    return merged;
  }, [fetchedStates, realtime.states]);

  const refreshGateway = async () => {
    setRefreshing(true);
    // Re-reads last_seen; it reports its own loading and cannot be awaited.
    gateways.reload();
    try {
      const states = await Promise.all(
        (data?.devices ?? []).map((device) => api.getDeviceState(worldId, device.device_id))
      );
      setFetchedStates(Object.fromEntries(states.map((state) => [state.device_id, state])));
    } finally {
      setRefreshing(false);
    }
  };

  // A gateway that has announced itself but predates the registry still belongs
  // in the list, so the two sources are unioned rather than one trusted.
  const gatewayIds = useMemo(
    () =>
      [
        ...new Set([...(gateways.data ?? []).map((gateway) => gateway.gateway_id), ...Object.keys(realtime.gateways)]),
      ].sort(),
    [gateways.data, realtime.gateways]
  );

  // Opening a rail view shows its detail at once; the list then narrows it.
  // Derived rather than stored so a selection cannot outlive the view it
  // belongs to.
  const shownBomSection = view === 'deploy' ? (bomSection ?? 'bom') : undefined;

  // The detail covers the viewport, so it leaves no room for the per-device
  // inspector.
  const detailOpen = shownBomSection !== undefined;

  const viewLabel = ACTIVITY_ITEMS.find((item) => item.id === view)?.label ?? '';

  const selected = devices.find((device) => device.device_id === selectedId);

  const select = (deviceId: string | undefined) => {
    // A proposed device is a drawing, not a device: clicking one must not put
    // an id in the URL that no inspector, and no write, could resolve.
    if (deviceId !== undefined && !data?.devices.some((device) => device.device_id === deviceId)) {
      return;
    }
    // Choosing a device is also asking to look at it, so the shared panel turns
    // to the inspector rather than leaving the choice with nothing to show for it.
    if (deviceId !== undefined) {
      setInspecting(true);
    }
    setSearchParams((params) => {
      if (deviceId === undefined) {
        params.delete('device');
      } else {
        params.set('device', deviceId);
      }
      return params;
    });
  };

  const agent = useDeviceAgent({ socket: realtime.socket, subscribe: realtime.subscribe });

  const digest = useCallback(
    () =>
      digestDevices({
        devices,
        poses: rigs.poses,
        states: realtime.states,
        optics: optics.optics,
        bounds,
        selected: selectedId,
        coverage: coverage.stats && digestCoverage(coverage.stats, coverageSettings, placement),
      }),
    [
      devices,
      rigs.poses,
      realtime.states,
      optics.optics,
      bounds,
      selectedId,
      coverage.stats,
      coverageSettings,
      placement,
    ]
  );

  const runCameraOps = useCallback(
    async (ops: readonly DeviceOp[]) => {
      const errors = await applyCameraOps(ops, {
        api,
        worldId,
        devices,
        poses: rigs.poses,
        bounds,
      });
      // Rows and shadows both moved; the list, the rigs and the BOM all read
      // from the reload rather than from anything this batch remembers.
      reload();
      return errors;
    },
    [api, worldId, devices, rigs.poses, bounds, reload]
  );

  const removeCamera = async (deviceId: string) => {
    await api.deleteDevice(worldId, deviceId);
    setSearchParams((params) => {
      params.delete('device');
      return params;
    });
    reload();
  };

  if (error) {
    return (
      <Box className="min-h-screen">
        <WorldHeader worldId={worldId} />
        <Box className="p-6">
          <Alert severity="error" action={<Button onClick={reload}>Retry</Button>}>
            {error.message}
          </Alert>
        </Box>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box className="min-h-screen">
        <WorldHeader worldId={worldId} />
        <Box className="p-6">{loading && <CircularProgress />}</Box>
      </Box>
    );
  }

  return (
    <Box className="flex h-screen flex-col">
      {/* The world is already loaded here, so the header takes its name rather
          than fetching one of its own. */}
      <WorldHeader worldId={worldId} name={data.world.name} />

      <Box className="flex min-h-0 flex-1">
        {/* Scene is the one entry that leaves: scenery is built on its own page,
            and this one owns devices (devplan §4). A world with nothing
            published yet has no URL to link to, so that entry asks the service
            for the scene instead. */}
        <ActivityBar
          active={view}
          linkTo={
            data.world.scene_id
              ? { scene: `/worlds/${worldId}/scenes/${encodeURIComponent(data.world.scene_id)}/edit` }
              : undefined
          }
          onSelect={(next) => (next === 'scene' ? void openScene() : setView(next))}
        />

        {view !== undefined &&
          (sideOpen ? (
            <Box
              className="flex w-64 shrink-0 flex-col border-r"
              sx={{ bgcolor: 'background.paper', borderColor: 'divider' }}
            >
              {view === 'deploy' ? (
                <DeployPanel
                  worldName={data.world.name}
                  devices={data.devices}
                  poses={rigs.poses}
                  selected={shownBomSection}
                  onSelect={setBomSection}
                  onHide={() => setSideOpen(false)}
                />
              ) : (
                <DeviceListPanel
                  devices={data.devices}
                  selectedId={selectedId}
                  states={realtime.states}
                  onSelect={select}
                  onAdd={() => setAddingCamera(true)}
                  onHide={() => setSideOpen(false)}
                />
              )}
            </Box>
          ) : (
            <CollapsedRail side="left" label={viewLabel} onExpand={() => setSideOpen(true)} />
          ))}

        <Box className="relative flex min-w-0 flex-1">
          <BabylonCanvas
            className="min-w-0 flex-1 outline-none"
            onReady={(context) => {
              editorRef.current = context.editor;
              const release = scene.onReady(context);
              if (context.rigs) {
                rigs.attach(context.rigs);
              }
              preview.attach(context);
              coverage.attach(context);
              capture.attach(context);
              return release;
            }}
            onTransformChanged={rigs.onTransformChanged}
            onSelectCamera={select}
          />

          {!detailOpen && <ViewAngleButtons editorRef={editorRef} />}

          {!detailOpen && (
            <CoveragePanel
              enabled={coverageOn}
              onEnabledChange={setCoverageOn}
              settings={coverageSettings}
              onSettingsChange={setCoverageSettings}
              placement={placement}
              onPlacementChange={setSubjectMoved}
              stats={coverage.stats}
            />
          )}

          {!detailOpen && (
            <ChatPanel
              digest={digest}
              apply={runCameraOps}
              requestOps={agent.requestOps}
              suggestions={CAMERA_STUB_PROMPTS}
              placeholder="Describe where the devices go"
              // A device is a real Thing with a real shadow, so creating or
              // destroying one is never applied without a click (devplan §7.4).
              needsConfirmation={(ops) => ops.some(touchesTwin)}
              onPending={setProposed}
              onNewThread={agent.newThread}
              streaming={agent.streaming}
              alternate={{
                label: 'Device',
                showing: inspecting,
                onShow: setInspecting,
                content: (
                  <DeviceInspector
                    device={selected}
                    pose={selectedId ? rigs.poses[selectedId] : undefined}
                    onPoseChange={selectedId ? (pose) => rigs.onTransformChanged(selectedId, pose) : undefined}
                    look={look}
                    optics={
                      selectedId && optics.optics[selectedId]
                        ? {
                            value: optics.optics[selectedId],
                            onChange: (change) => optics.set(selectedId, change),
                            pending: optics.pending[selectedId],
                            error: optics.errors[selectedId],
                            depthOfFieldShown,
                            onDepthOfFieldShownChange: setDepthOfFieldShown,
                          }
                        : undefined
                    }
                    previewCanvasRef={preview.canvasRef}
                    onDelete={removeCamera}
                  />
                ),
              }}
            />
          )}

          {shownBomSection === 'gateway' ? (
            <GatewayDetail
              gatewayId={gatewayIds[0]}
              status={gatewayIds[0] ? realtime.gateways[gatewayIds[0]] : undefined}
              worldName={data.world.name}
              devices={data.devices}
              states={gatewayStates}
              lastSeen={gateways.data?.find((gateway) => gateway.gateway_id === gatewayIds[0])?.last_seen}
              onRefresh={() => void refreshGateway()}
              refreshing={refreshing}
              onDownloadConfig={() => setDownloadingBundle(true)}
            />
          ) : (
            shownBomSection && (
              <BomDetail section={shownBomSection} devices={data.devices} poses={rigs.poses} states={realtime.states} />
            )
          )}
        </Box>
      </Box>

      <AddDeviceDialog worldId={worldId} open={addingCamera} onClose={() => setAddingCamera(false)} onAdded={reload} />
      <GatewaySetupDialog worldId={worldId} open={downloadingBundle} onClose={() => setDownloadingBundle(false)} />

      {/* A scene that will not load leaves the grid on screen, which on its own
          looks like a world nobody has built yet. */}
      <Snackbar
        open={sceneError !== undefined}
        autoHideDuration={10000}
        onClose={() => setSceneErrorSeen(scene.state.error)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity="error" onClose={() => setSceneErrorSeen(scene.state.error)}>
          This world's scene could not be opened. {sceneError?.message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={openError !== undefined}
        autoHideDuration={10000}
        onClose={() => setOpenError(undefined)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity="error" onClose={() => setOpenError(undefined)}>
          {openError}
        </Alert>
      </Snackbar>
    </Box>
  );
};
